import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { Box } from 'apps/sigma2-ui/src/components/ui/Box';
import { HorizontalFilterBar } from 'apps/sigma2-ui/src/filters-v2/components/HorizontalFilterBar';
import { useFilters } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { UseFiltersHook } from 'apps/sigma2-ui/src/filters-v2/lib/useFilters';
import { useCustomViz } from 'apps/sigma2-ui/src/components/custom-viz/hooks/useCustomViz';
import { getPrimaryFilterSelectionsFromRequest } from 'libs/end-to-end/src/progress-page-utils';
import { getProgressPageWhiteLabelingSubjectPlural } from 'libs/end-to-end/src/progress-page-utils';
import { getFocusArea } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getSecondaryFilterSelectionsFromCustomVizFormat } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { PrimaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { SecondaryFilterSelection } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { getDimensionFilterValue } from 'libs/end-to-end/src/progress-page-utils';
import { parseProgressData } from 'libs/end-to-end/src/progress-page-utils';
import { MyObservation } from 'libs/end-to-end/src/my-observation';
import { getFiltersFromSecondaryFilterSelections } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { OnApplyFnArgs } from 'libs/end-to-end/src/filters-v2/filter-utils';
import { ProgressBarChart } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressBarChart';
import { DisparityField } from 'libs/end-to-end/src/progress';
import { ProgressOutput } from 'libs/end-to-end/src/progress';
import { parseCustomVizResult } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { Card } from 'apps/sigma2-ui/src/components/ui/Card';
import { NotEnoughData } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageErrorStates';
import { HasFailure } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressPageErrorStates';
import { useDimensionConfig } from 'apps/sigma2-ui/src/lib/hooks/useDimensionConfig';
import { ButtonGroup } from 'apps/sigma2-ui/src/components/ui/ButtonGroup';
import { BiasAnalysisRequest } from 'libs/end-to-end/src/custom-viz/BiasAnalysisTypes';
import { callGenerateShareLink } from 'apps/sigma2-ui/src/state/generate-share-link';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { ProgressExplanation } from 'apps/sigma2-ui/src/pages/ProgressPage/ProgressExplanation';
import { S2GroupedBarChartUnit } from 'libs/end-to-end/src/grouped-bar';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { GetCustomVizRequestArgs } from 'apps/sigma2-ui/src/filters-v2/components/SaveAnalysisButton';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { ButtonGroupOption } from 'apps/sigma2-ui/src/components/ui/ButtonGroup';
import { DisparityFields } from 'libs/end-to-end/src/progress';
import { getProgressPageMetricDisplayName } from 'libs/end-to-end/src/progress-page-utils';
import { getProgressPageUnits } from 'libs/end-to-end/src/progress-page-utils';
import { CustomVizHookResult } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { CustomVizHookStatus } from 'apps/sigma2-ui/src/lib/custom-viz/types';
import { useObservation } from 'apps/sigma2-ui/src/state/observation';
import { ProgressRequest } from 'libs/end-to-end/src/custom-viz/ProgressTypes';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { UserAnalysesOverlay } from 'apps/sigma2-ui/src/components/UserAnalysesOverlay';
import { CustomVizCircularSpinner } from 'apps/sigma2-ui/src/components/ui/CustomVizCircularSpinner';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { convertTranslatedContentToReactNode } from 'apps/sigma2-ui/src/lib/hooks/useTranslation';
import { ExpandableExplanation } from 'apps/sigma2-ui/src/components/explanations/ExpandableExplanation';
import { getMetricDisplayNameFromObservation } from 'libs/end-to-end/src/me';
import { getExplanationForProgress } from 'libs/end-to-end/src/progress-explanation-utils';
import { getProgressPageExplanationAttributes } from 'libs/end-to-end/src/progress-explanation-utils';
import { DataRefreshIndicator } from 'apps/sigma2-ui/src/components/custom-viz/DataRefreshIndicator';
import { isEntitledToBiasAnalysis } from 'libs/end-to-end/src/group-entitlements';
import { useEmbedMode } from 'apps/sigma2-ui/src/components/EmbedModeContext';

/**
 * Component that displays a spinner while the progress chart is loading.
 */
export const ProgressSpinner = ({ isLoading }: { isLoading: boolean }) => {
  return (
    isLoading && (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        h="full"
        w="full"
        flex="1"
      >
        <CustomVizCircularSpinner />
      </Box>
    )
  );
};

/**
 * Handles filters update based on url changes (share link).
 */
export const ProgressUserOverlay = ({
  filterPayload,
  onAnalyzeWithParams,
}: {
  filterPayload: UseFiltersHook;
  onAnalyzeWithParams: ({
    primaryFilterSelections,
    secondaryFilterSelections,
  }: {
    primaryFilterSelections: PrimaryFilterSelection[];
    secondaryFilterSelections: SecondaryFilterSelection[];
  }) => void;
}) => {
  const { myObservation } = useObservation();

  const onShareLinkLoaded = useCallback(
    (request: ProgressRequest | null) => {
      if (!request || !myObservation) {
        return;
      }

      const extras = request.inputs.extras;
      const filters = extras.filters;
      const focusArea = extras.focusArea;

      const secondaryFilterSelections =
        getSecondaryFilterSelectionsFromCustomVizFormat({
          feature: 'progressPage',
          focusArea: request.inputs.extras.focusArea,
          customVizFilters: filters,
          focusAreas: myObservation.focusAreas,
          filterSetDefinitionsWithValues:
            myObservation.filterSetDefinitionsWithValues,
          dimensionDefinitions: myObservation.dimensions,
          analysisName: undefined,
          pipelineAnalysisConfiguration:
            myObservation.pipelineAnalysisConfiguration,
        });

      const primaryFilterSelections = getPrimaryFilterSelectionsFromRequest({
        request,
      });

      filterPayload.setFilters({
        primaryFilterSelections,
        secondaryFilterSelections,
        focusArea,
      });

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [filterPayload, myObservation, onAnalyzeWithParams],
  );

  const { userAuthenticatedApiCall, myUser } = useUser();

  useShareLinkRequest({
    onLoaded: onShareLinkLoaded,
    myUser,
    canLoad: filterPayload.isInitialized,
    userAuthenticatedApiCall,
    type: ProgressRequest,
  });

  const onShareLinkLoadedFn = useCallback(
    (request: CustomVizRequest | null) => {
      if (!request || request.customVizId !== 'progress') {
        return;
      }

      return onShareLinkLoaded(request);
    },
    [onShareLinkLoaded],
  );

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded: onShareLinkLoadedFn,
    resetFiltersToInitialState: filterPayload.resetFiltersToInitialState,
    resetMetricsToInitialState: null,
    resetDimensionsToInitialState: null,
  });

  return <UserAnalysesOverlay onUserAnalysisCancel={onUserAnalysisCancel} />;
};

export const ProgressPageInterface = ({
  organizationId,
  myObservation,
}: {
  organizationId: string;
  myObservation: MyObservation | null;
}) => {
  const context = useCustomViz();
  const { doViz } = context;

  const getCustomVizRequest = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      GetCustomVizRequestArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >): { customVizRequest: ProgressRequest | undefined } => {
      const focusArea = getFocusArea(primaryFilterSelections);
      const dimension = getDimensionFilterValue(primaryFilterSelections);

      if (!focusArea || !dimension || !myObservation?.type) {
        return { customVizRequest: undefined };
      }

      const filters = getFiltersFromSecondaryFilterSelections({
        secondaryFilterSelections,
      });

      const customVizRequest: ProgressRequest = {
        customVizId: 'progress',
        organizationId,
        observationLocationType: myObservation?.type,
        inputs: {
          extras: {
            focusArea: focusArea.focusArea,
            dimension,
            metric: focusArea.metric,
            filters: filters,
          },
          dropdowns: {},
        },
      };

      return {
        customVizRequest,
      };
    },
    [myObservation?.type, organizationId],
  );

  const shareLinkRawData = useShareLinkSearchParameter();

  const onAnalyzeWithParams = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
    }: Pick<
      OnApplyFnArgs,
      'primaryFilterSelections' | 'secondaryFilterSelections'
    >) => {
      const { customVizRequest } = getCustomVizRequest({
        primaryFilterSelections,
        secondaryFilterSelections,
      });

      if (!customVizRequest) {
        return;
      }

      doViz(customVizRequest);
    },
    [doViz, getCustomVizRequest],
  );

  const onQueryProgress = useCallback(
    ({
      primaryFilterSelections,
      secondaryFilterSelections,
      isProgrammaticChange,
      isInitialApply,
    }: OnApplyFnArgs) => {
      if (
        isProgrammaticChange ||
        (isInitialApply && shareLinkRawData !== null)
      ) {
        return;
      }

      onAnalyzeWithParams({
        primaryFilterSelections,
        secondaryFilterSelections,
      });
    },
    [onAnalyzeWithParams, shareLinkRawData],
  );

  const filterPayload = useFilters({
    feature: 'progressPage',
    focusArea: null,
    disabled: false,
    metricDefinitionWhiteLabeling: null,
    onApply: onQueryProgress,
    getCustomVizRequest,
    analysisName: undefined,
  });

  const { customVizRequest: lastCustomVizRequest } = useMemo(
    () =>
      getCustomVizRequest({
        primaryFilterSelections: filterPayload.primaryFilterSelections,
        secondaryFilterSelections: filterPayload.secondaryFilterSelections,
      }),
    [
      filterPayload.primaryFilterSelections,
      filterPayload.secondaryFilterSelections,
      getCustomVizRequest,
    ],
  );

  return (
    <Spacing size={'md'} direction={'vertical'}>
      <HorizontalFilterBar
        items={filterPayload.items}
        slotRight={
          <DataRefreshIndicator
            status={context.status}
            resultCreatedAt={context.resultCreatedAt}
          />
        }
      />

      <ProgressUserOverlay
        filterPayload={filterPayload}
        onAnalyzeWithParams={onAnalyzeWithParams}
      />

      <Card paddingClassName="p-0" className="h-full min-h-96">
        <ProgressChartCard
          customVizRequest={lastCustomVizRequest}
          result={context.result}
          status={context.status}
        />
      </Card>
    </Spacing>
  );
};

// eslint-disable-next-line complexity
export const ProgressChartCard = ({
  customVizRequest,
  result,
  status,
}: {
  customVizRequest: ProgressRequest | undefined;
  result: CustomVizHookResult;
  status: CustomVizHookStatus;
}) => {
  const { myObservation } = useObservation();

  const { data, isLoading, readyToDisplayDynamicContent, hasFailed } =
    useMemo(() => {
      return parseCustomVizResult({
        result,
        status,
        ResultZodType: ProgressOutput,
      });
    }, [result, status]);

  const dimensionConfig = useDimensionConfig();

  const dimensionComparisonGroup = useMemo(
    () =>
      customVizRequest
        ? dimensionConfig.getPrivilegedLevelForDimension(
            customVizRequest.inputs.extras.dimension,
          )
        : null,
    [customVizRequest, dimensionConfig],
  );

  const metricDisplayName = useMemo(() => {
    return getProgressPageMetricDisplayName({
      data,
      myObservation,
    });
  }, [data, myObservation]);

  const whiteLabelingSubjectPlural = useMemo(() => {
    return getProgressPageWhiteLabelingSubjectPlural({
      data,
      myObservation,
    });
  }, [data, myObservation]);

  const [selectedDisparity, setSelectedDisparity] =
    useState<DisparityField>('rateGroups');

  const disparityMenuOptions = useDisparityMenuOptions({
    metric: metricDisplayName,
    noun: 'average',
    selected: selectedDisparity,
    subjectPlural: whiteLabelingSubjectPlural,
  });

  useEffect(() => {
    if (!selectedDisparity && disparityMenuOptions.length > 0) {
      setSelectedDisparity(DisparityField.parse(disparityMenuOptions[0].value));
    }
  }, [selectedDisparity, disparityMenuOptions, setSelectedDisparity]);

  const selectedDisparityIndex =
    disparityMenuOptions.findIndex((o) => o.value === selectedDisparity) ?? 0;

  const chartData = useMemo(() => {
    if (!readyToDisplayDynamicContent) {
      return null;
    }

    return parseProgressData({
      data,
      disparity: selectedDisparity,
      getLevelByColumnValue: dimensionConfig.getLevelByColumnValue,
    });
  }, [data, dimensionConfig, readyToDisplayDynamicContent, selectedDisparity]);

  const units: S2GroupedBarChartUnit = useMemo(() => {
    return getProgressPageUnits({
      data,
      selectedDisparityIndex,
      myObservation,
    });
  }, [data, selectedDisparityIndex, myObservation]);

  const { userAuthenticatedApiCall } = useUser();
  const isEmbedMode = useEmbedMode();

  const getLinkToDeepDive = useCallback(
    async (treatmentDimensionLevel: string, year: string) => {
      if (!data || !myObservation || !customVizRequest || isEmbedMode) {
        return undefined;
      }

      const { metric, dimension, focusArea } = data;

      // Make sure they are entitled to see bias analysis for this metric, otherwise return nothing.
      if (
        !isEntitledToBiasAnalysis({
          features: myObservation.features,
          focusArea: focusArea,
        })
      ) {
        return undefined;
      }

      const secondaryFiltersPayload = [
        ...customVizRequest.inputs.extras.filters,
      ];

      secondaryFiltersPayload.push({
        columnHeaderValue: 'year',
        values: [year],
      });

      const request: BiasAnalysisRequest = {
        inputs: {
          dropdowns: {},
          extras: {
            metric,
            dimension,
            focusArea,
            filters: secondaryFiltersPayload,
            treatmentDimensionLevel,
          },
        },
        customVizId: 'biasAnalysis',
        organizationId: customVizRequest.organizationId,
        observationLocationType: myObservation.type,
      };

      const shareLinkResult = await callGenerateShareLink({
        windowUrl: window.location.origin,
        customVizRequest: request,
        userAuthenticatedApiCall,
      });
      if (!shareLinkResult) {
        return undefined;
      }

      return shareLinkResult.shareLink;
    },
    [
      customVizRequest,
      data,
      isEmbedMode,
      myObservation,
      userAuthenticatedApiCall,
    ],
  );

  const downloadContainerRef = useRef<HTMLDivElement>(null);

  const analytics = useAnalytics();

  const showNewExplanations = useMemo(
    () => myObservation?.showNewExplanations ?? false,
    [myObservation?.showNewExplanations],
  );

  useEffect(() => {
    const parsedSelectedDisparity = DisparityField.safeParse(selectedDisparity);
    if (parsedSelectedDisparity.success) {
      analytics.trackEvent({
        eventName: 'Progress Page Viewed',
        eventProperties: {
          selectedTab: parsedSelectedDisparity.data,
        },
      });
    }
  }, [analytics, selectedDisparity]);

  const overviewExplanation = useMemo(() => {
    if (!data) {
      return;
    }

    return getExplanationForProgress({
      explanations: data?.explanations,
      data,
      selectedDisparity,
      ...getProgressPageExplanationAttributes({
        data,
        myObservation,
        comparisonGroup: dimensionComparisonGroup?.columnValue ?? '',
      }),
    });
  }, [
    data,
    dimensionComparisonGroup?.columnValue,
    myObservation,
    selectedDisparity,
  ]);

  const dimensionsConfigPayload = useDimensionConfig();

  const dimensionDefinition = useMemo(() => {
    if (!data?.dimension) {
      return null;
    }

    return (
      dimensionsConfigPayload.getDimensionByColumnHeaderValue(
        data?.dimension,
      ) ?? null
    );
  }, [dimensionsConfigPayload, data?.dimension]);

  const metricDisplayNameForExplanation = useMemo(() => {
    if (!data?.metric || !data.focusArea || !myObservation) {
      return null;
    }

    return getMetricDisplayNameFromObservation({
      metricName: data.metric,
      focusArea: data.focusArea,
      myObservation,
    });
  }, [data?.metric, data?.focusArea, myObservation]);

  return (
    <div className="w-full h-full">
      <ProgressSpinner isLoading={isLoading} />
      {!isLoading && !readyToDisplayDynamicContent && !hasFailed && (
        <NotEnoughData />
      )}

      {!isLoading && hasFailed && <HasFailure />}

      {chartData && !isLoading && (
        <div
          ref={downloadContainerRef}
          className="divide-y-[1px] divide-gray-200 divide-solid flex flex-col gap-2"
        >
          <Box display="flex" flexDirection="col" alignItems="center" p="sm">
            {showNewExplanations && overviewExplanation && (
              <ExpandableExplanation
                headline={`${metricDisplayNameForExplanation} by ${dimensionDefinition?.displayName}`}
                overview={overviewExplanation.overview}
                details={overviewExplanation.description}
                className={'self-start py-4 px-6'}
                disableBorder={true}
              />
            )}
            <ButtonGroup
              options={disparityMenuOptions}
              onChange={(newValue: string) => {
                setSelectedDisparity(DisparityField.parse(newValue));
              }}
            />
            <ProgressBarChart
              data={chartData}
              metricDisplayName={metricDisplayName ?? ''}
              whiteLabelingSubjectPlural={whiteLabelingSubjectPlural}
              units={units}
              getLinkToDeepDive={getLinkToDeepDive}
              comparisonGroupValue={dimensionComparisonGroup?.columnValue ?? ''}
              onLoad={(selectedLevels) => {
                analytics.trackEvent({
                  eventName: 'Progress Filter By Level',
                  eventProperties: {
                    selectedLevels,
                  },
                });
              }}
              viewConfiguration={{}}
              isDisparities={true}
            />
          </Box>
          {data && !showNewExplanations && (
            <ProgressExplanation
              data={data}
              selectedDisparity={selectedDisparity}
              comparisonGroup={dimensionComparisonGroup?.columnValue ?? ''}
              myObservation={myObservation}
              downloadContainerRef={downloadContainerRef}
            />
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Return translated disparity menu options based on the metric
 */
export const useDisparityMenuOptions = ({
  metric,
  noun,
  selected,
  subjectPlural,
}: {
  metric: string | null;
  noun: string;
  selected: string;
  subjectPlural: string;
}): ButtonGroupOption[] => {
  const language = useLanguage();

  return useMemo(() => {
    if (!metric) {
      return [];
    }

    return DisparityFields.map((key) => {
      if (key === 'rateGroups') {
        return {
          text: metric,
          value: key,
          tooltip: convertTranslatedContentToReactNode(
            language.rateGroupsMeaning({ noun, metric }),
          ),
          selected: selected === 'rateGroups',
        } satisfies ButtonGroupOption;
      }

      if (key === 'rawDisparityGroups') {
        return {
          text: convertTranslatedContentToReactNode(language.rawDisparity()),
          value: 'rawDisparityGroups',
          tooltip: convertTranslatedContentToReactNode(
            language.rawDisparityMeaning({
              metricDisplayName: metric,
              subjectPlural,
            }),
          ),
          selected: selected === 'rawDisparityGroups',
        } satisfies ButtonGroupOption;
      }

      if (key === 'controlledDisparityGroups') {
        return {
          text: convertTranslatedContentToReactNode(
            language.controlledDisparity(),
          ),
          value: 'controlledDisparityGroups',
          tooltip: convertTranslatedContentToReactNode(
            language.controlledDisparityMeaning({
              metricDisplayName: metric,
              subjectPlural,
            }),
          ),
          selected: selected === 'controlledDisparityGroups',
        } satisfies ButtonGroupOption;
      }

      return null;
    }).filter((opt) => opt !== null);
  }, [metric, language, noun, selected, subjectPlural]);
};
